import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { api } from '@/shared/lib/api'
import { showErrorAlert } from '@/shared/lib/errors'
import { fileToBase64 } from '@/shared/lib/file/to-base64'
import { PageHeader, Screen } from '@/shared/ui/Layout'

import { useCreatePost } from '../model/mutations'
import { CreatePostForm } from '../ui/CreatePostForm'

// Media is user-scoped in the API (User::MediasController merges user_id only),
// so it uploads to /user/medias — not nested under the event. The post then
// just references the returned file URL.
//
// The `active_storage_base64` gem requires the `data` field to be a full data
// URI (`data:<mime>;base64,<payload>`) — it derives the content type from the
// URI header and ignores a bare payload. `fileToBase64` strips that prefix, so
// we rebuild it here. Matches the shape used by spec/factories/medias.rb.
const uploadMedia = async (file) => {
  const base64 = await fileToBase64(file)
  const dataUri = `data:${file.type};base64,${base64}`
  const { data } = await api.post('/user/medias', {
    record: {
      file: { data: dataUri, filename: file.name },
    },
  })
  return data?.record?.file?.url ?? null
}

// Backend requires a non-null `title` and a `post_type` of text|video|audio.
// The form only collects content + media, so we derive both here:
// - title  → first line of content, trimmed to 80 chars
// - post_type → the media type when a video/audio file is attached, else text
//   (an image lives on a text post via media_url)
const deriveTitle = (content) => {
  const firstLine = content.trim().split('\n')[0].trim()
  return firstLine.length > 80 ? `${firstLine.slice(0, 79).trimEnd()}…` : firstLine
}

const derivePostType = (mediaType, hasFile) =>
  hasFile && (mediaType === 'video' || mediaType === 'audio') ? mediaType : 'text'

export const CreatePostPage = () => {
  const { eventSlug } = useDigitalProductContext()
  const { createPost } = useCreatePost(eventSlug)
  const router = useAppRouter()

  const handleSubmit = async ({ content, media_type, media }) => {
    try {
      const media_url = media ? await uploadMedia(media) : undefined
      await createPost({
        content,
        title: deriveTitle(content),
        post_type: derivePostType(media_type, !!media),
        media_url,
      })
      router.back()
    } catch (err) {
      showErrorAlert(err)
    }
  }

  return (
    <Screen>
      <Screen.Context>
        <PageHeader title="Nova publicação" backHref="/feed" />
      </Screen.Context>
      <Screen.Content className="pt-4 pb-8">
        <CreatePostForm onSubmit={handleSubmit} onClose={() => router.back()} />
      </Screen.Content>
    </Screen>
  )
}
